import { useSiteCategories } from 'api/goodtrust/site'
import { ItemGrid } from 'components/Grid'
import { Icon } from 'components/icon'
import {
  SOrderSiteListItem,
  SOrderSiteListItemCancel,
  SOrderSiteListItemLogo,
} from './OrderSiteList.styled'
import { BlockSpinner } from 'components/spinner/Spinner'
import { Stack } from 'components/Stack'
import { WizardGridItem } from 'components/wizard/wizardGridItem/WizardGridItem'
import { WizardSubtitle } from 'components/wizard/WizardTypography'
import { Fragment } from 'react'
import { colors } from 'utils/colors'
import { ApiType } from 'utils/types'
import { isSiteCategory } from 'components/order/util'
import { isNonNullable } from 'utils/general'
import { useTranslation } from 'next-i18next'

export function OrderSiteList(props: {
  className?: string
  values?: string[]
  search?: string
  onNewSiteClick?: () => void
  onRemove?: (uuid: string) => void
  onSelect: (item: {
    uuid: string
    title: string
    category?: string
    logo?: ApiType['FileResponse'] | null
  }) => void
}) {
  const { data } = useSiteCategories()
  const { t } = useTranslation('device')

  const orderedCategories = Object.entries(data?.json ?? {}).sort(
    (a, b) => (a[1]?.position ?? 0) - (b[1]?.position ?? 0)
  )

  const filteredCategoriesThatHasTranslation = orderedCategories
    .map((category) => {
      const categoryName = category[0].toLowerCase()
      const payload = category[1]
      if (categoryName && isSiteCategory(categoryName)) {
        return [categoryName, payload] as const
      } else {
        return undefined
      }
    })
    .filter(isNonNullable)

  return (
    <Fragment>
      {data?.json ? (
        <Stack space="1.25rem">
          {filteredCategoriesThatHasTranslation.map(([categoryName, payload]) => {
            const filteredSites = payload.sites?.filter((site) => {
              if (!props.search) return site
              return site.title?.toLocaleLowerCase()?.includes(props.search.toLocaleLowerCase())
            })

            if (!filteredSites?.length) return null
            return (
              <div key={categoryName}>
                <WizardSubtitle>{t(`device.store.categories.${categoryName}`)}</WizardSubtitle>
                <ItemGrid>
                  {
                    <Fragment>
                      {filteredSites?.map((item) => {
                        const isActive = item.uuid != null && props.values?.includes(item.uuid)
                        return (
                          <WizardGridItem
                            key={item.uuid}
                            data-testid={`site-item-${item.uuid}`}
                            active={isActive}
                            css={isActive && { paddingRight: 0 }}
                            image={
                              item?.logo?.url && (
                                <SOrderSiteListItemLogo
                                  src={item?.logo?.url}
                                  width={36}
                                  height={36}
                                  alt={item.title || 'logo'}
                                />
                              )
                            }
                            onClick={() => {
                              if (item.uuid && item.title) {
                                return props.onSelect?.({
                                  uuid: item.uuid,
                                  title: item.title,
                                  logo: item.logo,
                                  category: categoryName,
                                })
                              }
                            }}
                          >
                            <SOrderSiteListItem>
                              {item.title}
                              {isActive && (
                                <SOrderSiteListItemCancel
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    props.onRemove?.(item.uuid)
                                  }}
                                >
                                  <Icon name="Close" color={colors.gray[500]} />
                                </SOrderSiteListItemCancel>
                              )}
                            </SOrderSiteListItem>
                          </WizardGridItem>
                        )
                      })}
                    </Fragment>
                  }
                </ItemGrid>
              </div>
            )
          })}
        </Stack>
      ) : (
        <BlockSpinner />
      )}
    </Fragment>
  )
}
